import { useMutation } from '@apollo/client';

import { cacheUtils, eluveAdminHasuraContext } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const userFragment = graphql(`
  fragment User on Users @_unmask {
    __typename
    id
    firstName
    lastName
    email
  }
`);

export const reassignAppointmentOwnerMutation = graphql(`
  mutation reassignAppointmentOwner(
    $tenantId: uuid!
    $appointmentId: uuid!
    $newOwnerId: uuid!
  ) {
    updateAppointmentsByPk(
      pkColumns: { tenantId: $tenantId, id: $appointmentId }
      _set: { userId: $newOwnerId }
    ) {
      __typename
      id
      userId
      user {
        __typename
        id
        firstName
        lastName
        email
      }
    }
  }
`);

export const useReassignAppointmentOwner = () => {
  const toast = useToast();
  const [reassignAppointmentOwner] = useMutation(
    reassignAppointmentOwnerMutation,
    {
      context: eluveAdminHasuraContext,
      optimisticResponse: ({ appointmentId, newOwnerId }) => {
        // Try to read user data from cache
        const cachedUser = cacheUtils.readFragment({
          fragment: userFragment,
          key: { id: newOwnerId },
          strict: false,
        });

        return {
          updateAppointmentsByPk: {
            __typename: 'Appointments' as const,
            id: appointmentId,
            userId: newOwnerId,
            user: {
              __typename: 'Users' as const,
              id: newOwnerId,
              firstName: cachedUser?.firstName ?? '',
              lastName: cachedUser?.lastName ?? '',
              email: cachedUser?.email ?? '',
            },
          },
        };
      },
      errorPolicy: 'all',
      onError: (error) => {
        if (error.networkError) {
          toast.error(
            'Network error. Please check your connection and try again.',
          );
          return;
        }

        // Generic error fallback
        toast.error('Failed to change appointment owner. Please try again.');
      },
    },
  );

  return reassignAppointmentOwner;
};
